import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  Modal,
  TextInput,
  TimePicker,
} from '@carbon/react';
import moment from 'moment';
import axios from 'axios';

import {
  defaultScheduleMetadata,
  ISchedule,
  IScheduleRetryPolicy,
  ISchedulerProfile,
  ISlotDefaultConfig,
} from '../../../AppSlice';
import { IMonitor, ITimelineItem } from '../../inputs/Timeline/Timeline.model';
import XDDatePicker from '../../inputs/XDDatePicker';
import RecorderAdvancedConfigForm, {
  AdvancedConfig,
  AdvancedScheduleConfig,
  FilesystemResource,
  RtmpResource,
  SdiResource,
} from './RecorderAdvancedConfigForm';
import { GatewayIp, endpoints } from '../../../data/endpoints';

type Props = {
  initialSelectedChannelIds?: string[];
  existingItems?: ITimelineItem[];
  monitors?: IMonitor[];
  open: boolean;
  record: ITimelineItem;
  mediaengineProfiles?: ISchedulerProfile[];
  sdiResources?: SdiResource[];
  filesystemResources?: FilesystemResource[];
  rtmpResources?: RtmpResource[];
  slotActiveConfigs?: Record<string, ISlotDefaultConfig | null>;
  token?: string | null;
  onClose: () => void;
  onDelete: (id: string) => void;
  onSubmit: (item: ITimelineItem, schedule: Partial<ISchedule>) => void;
};
const MEDIAENGINE_TASK_TYPE = 'MEDIAENGINE_RECORD';
const DUMMY_HEARTBEAT_TASK_TYPE = 'DUMMY_HEARTBEAT';
const DEFAULT_HEARTBEAT_FILE = 'C:\\temp\\slotA_default.txt';
const DEFAULT_HEARTBEAT_MESSAGE = 'slotA_default';
const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const cloneValue = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
const toUtcIsoString = (value: string): string => moment(value).utc().toISOString();

const parseDefaultConfigParams = (config?: ISlotDefaultConfig | null) => {
  const params = config?.params ?? {};
  return {
    taskType: config?.taskType ?? MEDIAENGINE_TASK_TYPE,
    input: typeof params.input === 'string' ? params.input : (params.input?.url ?? params.inputUrl ?? '').toString(),
    output: typeof params.output === 'string' ? params.output : (params.output?.url ?? '').toString(),
    filename: (params.filename ?? '').toString(),
    profile: (params.profile ?? '').toString(),
    additionalOutputs: Array.isArray(params.additionalOutputs) ? cloneValue(params.additionalOutputs) : [],
    realtime: typeof params.realtime === 'boolean' ? params.realtime : true,
    additionalArgs: Array.isArray(params.additionalArgs) ? params.additionalArgs.join('\n') : '',
    heartbeatFile: (params.file ?? DEFAULT_HEARTBEAT_FILE).toString(),
    heartbeatIntervalMs: Number(params.intervalMs ?? 1000),
    heartbeatMessage: (params.message ?? DEFAULT_HEARTBEAT_MESSAGE).toString(),
  };
};

const createChannelConfig = (
  baseParams: Record<string, any>,
  channelId: string,
  channelUrl = '',
  slotDefaultConfig?: ISlotDefaultConfig | null
): Record<string, any> => {
  const parsedDefaultConfig = parseDefaultConfigParams(slotDefaultConfig);
  const resolvedTaskType = (
    baseParams?.taskType ??
    parsedDefaultConfig.taskType ??
    MEDIAENGINE_TASK_TYPE
  ).toString().trim() || MEDIAENGINE_TASK_TYPE;
  const input = baseParams?.input && typeof baseParams.input === 'object' && !Array.isArray(baseParams.input)
    ? cloneValue(baseParams.input)
    : cloneValue(defaultScheduleMetadata.params?.input ?? {});
  const output = baseParams?.output && typeof baseParams.output === 'object' && !Array.isArray(baseParams.output)
    ? cloneValue(baseParams.output)
    : cloneValue(defaultScheduleMetadata.params?.output ?? {});
  const inputResourceId = (input?.id ?? '').toString().trim();
  const normalizedInputType = (input?.type ?? (inputResourceId ? 'sdi' : 'rtmp')).toString().trim() || 'rtmp';
  const isSdiConfig =
    normalizedInputType === 'sdi' ||
    (input?.url ?? '').toString().trim().toLowerCase().startsWith('sdi://') ||
    inputResourceId.length > 0;
  const sourceId = isSdiConfig
    ? (input?.video?.sourceId ?? '').toString().trim()
    : channelId;
  const resolvedInputUrl = (
    input?.url ??
    baseParams?.inputUrl ??
    (typeof baseParams?.input === 'string' ? baseParams.input : '') ??
    parsedDefaultConfig.input ??
    channelUrl ??
    ''
  ).toString();
  const resolvedOutputUrl = (
    output?.url ??
    (typeof baseParams?.output === 'string' ? baseParams.output : '') ??
    parsedDefaultConfig.output ??
    ''
  ).toString();
  const additionalArgsValue =
    Array.isArray(baseParams?.additionalArgs)
      ? baseParams.additionalArgs
      : Array.isArray(baseParams?.extra)
        ? baseParams.extra
        : typeof baseParams?.additionalArgs === 'string'
          ? baseParams.additionalArgs.split(/\r?\n/)
          : parsedDefaultConfig.additionalArgs
            ? parsedDefaultConfig.additionalArgs.split(/\r?\n/)
            : [];
  const heartbeatIntervalMs = Number(baseParams?.intervalMs ?? parsedDefaultConfig.heartbeatIntervalMs ?? 1000);
  const additionalOutputs = Array.isArray(baseParams?.additionalOutputs)
    ? cloneValue(baseParams.additionalOutputs)
    : cloneValue(parsedDefaultConfig.additionalOutputs);

  const resolvedOutputResourceId = (baseParams?.outputResourceId ?? output?.id ?? '').toString();
  const resolvedInputResourceId = (baseParams?.inputResourceId ?? input?.id ?? inputResourceId ?? '').toString();

  return {
    taskType: resolvedTaskType,
    inputResourceId: resolvedInputResourceId,
    outputResourceId: resolvedOutputResourceId,
    input: {
      ...input,
      type: isSdiConfig ? 'sdi' : normalizedInputType,
      url: isSdiConfig
        ? ((input?.url ?? (sourceId ? buildSdiInputUrl(sourceId) : '')).toString())
        : resolvedInputUrl,
      ...(inputResourceId ? { id: inputResourceId } : {}),
      video: {
        ...(input?.video ?? {}),
        sourceId,
        formatId: isSdiConfig ? null : normalizeNullableString(input?.video?.formatId),
      },
      audio: {
        ...(input?.audio ?? {}),
        sourceId: isSdiConfig ? '' : (input?.audio?.sourceId ?? '').toString(),
        formatId: isSdiConfig ? null : normalizeNullableString(input?.audio?.formatId),
      },
    },
    output: {
      ...output,
      type: (output?.type ?? 'file').toString().trim() || 'file',
      url: resolvedOutputUrl,
    },
    filename: (baseParams?.filename ?? parsedDefaultConfig.filename ?? '').toString(),
    profile: (baseParams?.profile ?? parsedDefaultConfig.profile ?? '').toString(),
    additionalOutputs,
    additionalOutputEnabled: additionalOutputs.length > 0,
    additionalOutputResourceId: (additionalOutputs[0]?.output?.id ?? additionalOutputs[0]?.id ?? '').toString(),
    additionalOutputProfile: (additionalOutputs[0]?.profile ?? '').toString(),
    realtime:
      typeof baseParams?.realtime === 'boolean'
        ? baseParams.realtime
        : parsedDefaultConfig.realtime,
    additionalArgs: additionalArgsValue
      .map((value: unknown) => (value ?? '').toString())
      .filter((value: string) => value.trim().length > 0),
    heartbeatFile: (baseParams?.file ?? parsedDefaultConfig.heartbeatFile ?? DEFAULT_HEARTBEAT_FILE).toString(),
    heartbeatIntervalMs: Number.isFinite(heartbeatIntervalMs) ? heartbeatIntervalMs : 1000,
    heartbeatMessage: (baseParams?.message ?? parsedDefaultConfig.heartbeatMessage ?? DEFAULT_HEARTBEAT_MESSAGE).toString(),
  };
};

const normalizeMonitorIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (item ?? '').toString().trim())
    .filter((item, index, array) => item.length > 0 && array.indexOf(item) === index);
};

const extractMonitorIdFromAdditionalArgs = (additionalArgs: unknown): string => {
  if (!Array.isArray(additionalArgs)) return '';
  const monitorArg = additionalArgs.find(
    (arg) => arg && typeof arg === 'object' && !Array.isArray(arg) &&
      typeof (arg as Record<string, any>).monitorId === 'string' &&
      (arg as Record<string, any>).monitorId.trim().length > 0
  ) as Record<string, any> | undefined;
  return (monitorArg?.monitorId ?? '').toString().trim();
};

const extractMonitorIdsFromAdditionalArgs = (additionalArgs: unknown): string[] => {
  if (!Array.isArray(additionalArgs)) return [];
  return additionalArgs
    .filter(
      (arg) => arg && typeof arg === 'object' && !Array.isArray(arg) &&
        typeof (arg as Record<string, any>).monitorId === 'string' &&
        (arg as Record<string, any>).monitorId.trim().length > 0
    )
    .map((arg) => ((arg as Record<string, any>).monitorId ?? '').toString().trim())
    .filter((item, index, array) => item.length > 0 && array.indexOf(item) === index);
};

const buildOutputPath = (outputDir: string, outputFilename: string): string => {
  const dir = outputDir.trim();
  const filename = outputFilename.trim();
  if (!dir) return filename;
  if (!filename) return dir;
  const separator = dir.includes('\\') && !dir.includes('/') ? '\\' : '/';
  const normalizedDir = dir.endsWith('/') || dir.endsWith('\\') ? dir.slice(0, -1) : dir;
  return `${normalizedDir}${separator}${filename}`;
};

const buildSdiInputUrl = (deviceIndex: unknown): string => {
  if (deviceIndex === '' || deviceIndex === null || deviceIndex === undefined) return '';
  const index = Number(deviceIndex);
  if (!Number.isFinite(index) || index < 0) return '';
  return `sdi://DeckLinkDevice(${index})`;
};

const parseSdiDeviceIndexFromUrl = (url: unknown): string => {
  if (url === null || url === undefined) return '';
  const value = url.toString().trim();
  if (!value.toLowerCase().startsWith('sdi://')) return '';
  const match = value.match(/DeckLinkDevice\((\d+)\)/i);
  return (match?.[1] ?? '').toString().trim();
};

const normalizeNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  const trimmed = value.toString().trim();
  return trimmed.length > 0 ? trimmed : null;
};

const createScheduleState = (): Partial<ISchedule> => ({
  ...defaultScheduleMetadata,
  params: { ...(defaultScheduleMetadata.params ?? {}) },
  retryPolicy: { ...(defaultScheduleMetadata.retryPolicy ?? {}) },
});

type ValidationMap = {
  label: boolean;
  monitor: boolean;
  start: boolean;
  end: boolean;
  inputUrl: boolean;
  outputUrl: boolean;
  profile: boolean;
};

const CreateRecordModal: React.FC<Props> = ({
  initialSelectedChannelIds = [],
  existingItems = [],
  monitors: monitorsProp = [],
  open,
  record,
  mediaengineProfiles = [],
  sdiResources = [],
  filesystemResources = [],
  rtmpResources = [],
  slotActiveConfigs = {},
  token = null,
  onClose,
  onDelete,
  onSubmit,
}) => {
  const monitors = monitorsProp;

  const [formData, setFormData] = useState<ITimelineItem | null>(record);
  const [scheduleData, setScheduleData] = useState<Partial<ISchedule>>(createScheduleState);
  const [startDate, setStartDate] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [activeSettingsChannelId, setActiveSettingsChannelId] = useState('');
  const [loadingInitialSchedule, setLoadingInitialSchedule] = useState(false);

  const titleInputRef = useRef<HTMLInputElement>(null);
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);

  const monitorOptions = useMemo(() => monitors ?? [], [monitors]);
  const primaryChannelId = selectedChannelIds[0] ?? '';
  const activeChannelId = activeSettingsChannelId || primaryChannelId;
  const nowMoment = useMemo(() => moment(), [formData?.id, formData?.start, formData?.end]);
  const formStartMoment = useMemo(() => (formData?.start ? moment(formData.start) : null), [formData?.start]);
  const formEndMoment = useMemo(() => (formData?.end ? moment(formData.end) : null), [formData?.end]);
  const isExistingRecord = formData?.id !== 'new_0';
  const isFutureRecord = Boolean(isExistingRecord && formStartMoment?.isValid() && formStartMoment.isAfter(nowMoment));
  const isActiveRecord = Boolean(
    isExistingRecord &&
    formStartMoment?.isValid() &&
    formStartMoment.isSameOrBefore(nowMoment) &&
    formEndMoment?.isValid() &&
    formEndMoment.isAfter(nowMoment)
  );
  const isPastRecord = Boolean(
    isExistingRecord &&
    formEndMoment?.isValid() &&
    formEndMoment.isSameOrBefore(nowMoment)
  );
  const canEditAllInputs = !isExistingRecord || isFutureRecord;
  const canEditEndAt = canEditAllInputs || isActiveRecord;
  const allInputsReadOnly = isPastRecord;

  const setFormField = (field: keyof ITimelineItem, value: any) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const channelConfigs = useMemo<Record<string, Record<string, any>>>(
    () => ((scheduleData.params?.channelConfigs as Record<string, Record<string, any>>) ?? {}),
    [scheduleData.params?.channelConfigs]
  );

  const selectedTimeAvailableChannelIds = useMemo(() => {
    if (!formData?.start || !formData?.end) return monitorOptions.map((monitor) => monitor.id);
    const currentStart = moment(formData.start);
    const currentEnd = moment(formData.end);
    if (!currentStart.isValid() || !currentEnd.isValid()) return monitorOptions.map((monitor) => monitor.id);
    return monitorOptions.filter((monitor) => {
      const hasConflict = existingItems.some((item) => {
        if (!item?.channel || item.channel !== monitor.id || item.id === formData.id) return false;
        if (!item.start || !item.end) return false;
        const itemStart = moment(item.start);
        const itemEnd = moment(item.end);
        if (!itemStart.isValid() || !itemEnd.isValid()) return false;
        return currentStart.isBefore(itemEnd) && currentEnd.isAfter(itemStart);
      });
      return !hasConflict;
    }).map((monitor) => monitor.id);
  }, [existingItems, formData?.end, formData?.id, formData?.start, monitorOptions]);

  const visibleMonitorOptions = useMemo(
    () => monitorOptions.filter(
      (monitor) =>
        selectedTimeAvailableChannelIds.includes(monitor.id) ||
        selectedChannelIds.includes(monitor.id)
    ),
    [monitorOptions, selectedChannelIds, selectedTimeAvailableChannelIds]
  );

  const allVisibleChannelsSelected =
    visibleMonitorOptions.length > 0 &&
    visibleMonitorOptions.every((monitor) => selectedChannelIds.includes(monitor.id));

  const activeChannelConfig = useMemo(() => {
    if (!activeChannelId) return createChannelConfig(scheduleData.params ?? {}, '', '');
    const activeMonitor = monitorOptions.find((monitor) => monitor.id === activeChannelId);
    return (
      channelConfigs[activeChannelId] ??
      createChannelConfig(
        scheduleData.params ?? {},
        activeChannelId,
        activeMonitor?.ip ?? '',
        slotActiveConfigs[activeChannelId]
      )
    );
  }, [activeChannelId, channelConfigs, monitorOptions, scheduleData.params, slotActiveConfigs]);

  const activeAdvancedConfig = useMemo<AdvancedConfig>(() => {
    const config = activeChannelConfig;
    const taskType = (config?.taskType ?? MEDIAENGINE_TASK_TYPE).toString();
    const rawInputType = (config?.input?.type ?? 'rtmp').toString();
    const inputUrl = (config?.input?.url ?? '').toString();
    const rawInputId = (config?.input?.id ?? '').toString();
    const isSdi = rawInputType === 'sdi' || inputUrl.toLowerCase().startsWith('sdi://') || Boolean(rawInputId);
    const inputType = isSdi ? 'sdi' : rawInputType;
    const deviceIndex = (config?.input?.video?.sourceId ?? '').toString();

    const sdiResource = isSdi
      ? (rawInputId
          ? sdiResources.find((r) => r.id === rawInputId)
          : sdiResources.find((r) => {
              const di = r.config?.deviceIndex?.toString();
              return di !== undefined && di !== '' && di === deviceIndex;
            }))
      : undefined;
    const additionalOutput = Array.isArray(config?.additionalOutputs) ? config.additionalOutputs[0] : undefined;

    return {
      taskType,
      input: isSdi ? '' : inputUrl,
      inputType,
      inputResourceId: sdiResource?.id ?? rawInputId,
      output: (config?.output?.url ?? '').toString(),
      outputType: (config?.output?.type ?? 'file').toString(),
      outputResourceId: (config?.outputResourceId ?? config?.output?.id ?? '').toString(),
      additionalOutputEnabled: Boolean(config?.additionalOutputEnabled ?? additionalOutput?.output?.id ?? additionalOutput?.id),
      additionalOutputResourceId: (config?.additionalOutputResourceId ?? additionalOutput?.output?.id ?? additionalOutput?.id ?? '').toString(),
      additionalOutputProfile: (config?.additionalOutputProfile ?? additionalOutput?.profile ?? '').toString(),
      filename: (config?.filename ?? '').toString(),
      profile: (config?.profile ?? '').toString(),
      realtime: typeof config?.realtime === 'boolean' ? config.realtime : true,
      additionalArgs: Array.isArray(config?.additionalArgs)
        ? config.additionalArgs.join('\n')
        : '',
      heartbeatFile: (config?.heartbeatFile ?? DEFAULT_HEARTBEAT_FILE).toString(),
      heartbeatIntervalMs: Number(config?.heartbeatIntervalMs ?? 1000),
      heartbeatMessage: (config?.heartbeatMessage ?? DEFAULT_HEARTBEAT_MESSAGE).toString(),
    };
  }, [activeChannelConfig, sdiResources]);

  const taskLabel = useMemo(() => (formData?.title ?? '').toString().trim(), [formData?.title]);

  const profileOptions = useMemo(() => {
    return (mediaengineProfiles ?? []).filter((profile) => {
      const id = profile?.id?.toString().trim();
      const name = profile?.name?.toString().trim();
      return Boolean(id && name);
    });
  }, [mediaengineProfiles]);

  const validation = useMemo<ValidationMap>(() => {
    if (!formData) return { label: false, monitor: false, start: false, end: false, inputUrl: false, outputUrl: false, profile: false };

    const labelValid = taskLabel.length > 0;
    const monitorValid = selectedChannelIds.length > 0;
    const startValid =
      !!formData.start && moment(formData.start).isValid() &&
      (!canEditAllInputs || moment(formData.start).isAfter(nowMoment));
    const endValid =
      !!formData.end && moment(formData.end).isValid() && startValid &&
      moment(formData.end).isAfter(moment(formData.start)) &&
      (!canEditEndAt || moment(formData.end).isAfter(nowMoment));

    const perChannelConfigs = selectedChannelIds.map((channelId) => {
      const monitor = monitorOptions.find((item) => item.id === channelId);
      return (
        channelConfigs[channelId] ??
        createChannelConfig(scheduleData.params ?? {}, channelId, monitor?.ip ?? '', slotActiveConfigs[channelId])
      );
    });

    const inputUrlValid = perChannelConfigs.every((config) => {
      const taskType = (config?.taskType ?? scheduleData.taskType ?? defaultScheduleMetadata.taskType ?? '').toString();
      if (taskType === DUMMY_HEARTBEAT_TASK_TYPE) {
        return (config?.heartbeatFile ?? '').toString().trim().length > 0;
      }
      const channelInputType = (config?.input?.type ?? 'rtmp').toString().trim();
      const channelInputUrl = (config?.input?.url ?? '').toString().trim();
      const channelSourceId = (config?.input?.video?.sourceId ?? '').toString().trim();
      const sdiConfig = channelInputType === 'sdi' || channelInputUrl.toLowerCase().startsWith('sdi://');
      if (sdiConfig) {
        return channelSourceId.length > 0 ||
               (config?.inputResourceId ?? config?.input?.id ?? '').toString().trim().length > 0;
      }
      return channelInputUrl.length > 0;
    });

    const outputUrlValid = perChannelConfigs.every((config) => {
      const taskType = (config?.taskType ?? scheduleData.taskType ?? defaultScheduleMetadata.taskType ?? '').toString();
      if (taskType === DUMMY_HEARTBEAT_TASK_TYPE) {
        return (config?.heartbeatMessage ?? '').toString().trim().length > 0;
      }
      const primaryOutputValid =
        (config?.outputResourceId ?? config?.output?.id ?? '').toString().trim().length > 0 ||
        (config?.output?.url ?? '').toString().trim().length > 0;
      const additionalOutputValid = !config?.additionalOutputEnabled ||
        (config?.additionalOutputResourceId ?? config?.additionalOutputs?.[0]?.output?.id ?? '')
          .toString().trim().length > 0;
      return primaryOutputValid && additionalOutputValid;
    });

    const profileValid = perChannelConfigs.every((config) => {
      const taskType = (config?.taskType ?? scheduleData.taskType ?? defaultScheduleMetadata.taskType ?? '').toString();
      if (taskType !== MEDIAENGINE_TASK_TYPE) return true;
      const primaryProfileValid = (config?.profile ?? '').toString().trim().length > 0;
      const additionalProfileValid = !config?.additionalOutputEnabled ||
        (config?.additionalOutputProfile ?? config?.additionalOutputs?.[0]?.profile ?? '')
          .toString().trim().length > 0;
      return primaryProfileValid && additionalProfileValid;
    });

    return {
      label: labelValid,
      monitor: monitorValid,
      start: startValid,
      end: endValid,
      inputUrl: inputUrlValid,
      outputUrl: outputUrlValid,
      profile: profileValid,
    };
  }, [
    formData, channelConfigs, monitorOptions, nowMoment, selectedChannelIds,
    scheduleData.params, scheduleData.taskType, slotActiveConfigs, taskLabel,
    canEditAllInputs, canEditEndAt,
  ]);

  const isFormValid = useMemo(() => Object.values(validation).every((value) => value), [validation]);
  const isSubmitDisabled = useMemo(() => allInputsReadOnly || !isFormValid, [allInputsReadOnly, isFormValid]);
  const hasDestructiveSecondaryAction = isFutureRecord || isActiveRecord;

  const secondaryButtons = useMemo(() => {
    const buttons = [{ buttonText: "Cancel", onClick: () => { onClose(); } }];
    if (isFutureRecord) {
      buttons.push({ buttonText: "Delete", onClick: () => { onDelete(formData.id); } });
    } else if (isActiveRecord) {
      buttons.push({ buttonText: "Stop Recording", onClick: () => { onDelete(formData.id); } });
    }
    return buttons;
  }, [formData?.id, isActiveRecord, isFutureRecord, onClose, onDelete]);

  const ensureScheduleState = (prev?: Partial<ISchedule>): Partial<ISchedule> =>
    prev ?? createScheduleState();

  const updateSchedule = (updates: Partial<ISchedule>) => {
    setScheduleData((prev) => ({ ...ensureScheduleState(prev), ...updates }));
  };

  const updateScheduleParams = (
    updates: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)
  ) => {
    setScheduleData((prev) => {
      const base = ensureScheduleState(prev);
      const prevParams = { ...(base.params ?? {}) };
      const nextParams = typeof updates === 'function' ? updates(prevParams) : { ...updates };
      return { ...base, params: { ...prevParams, ...nextParams } };
    });
  };

  const updateChannelConfigs = (
    updates: Record<string, Record<string, any>> | ((prev: Record<string, Record<string, any>>) => Record<string, Record<string, any>>)
  ) => {
    updateScheduleParams((prev) => {
      const prevConfigs = { ...((prev.channelConfigs as Record<string, Record<string, any>>) ?? {}) };
      const nextConfigs = typeof updates === 'function' ? updates(prevConfigs) : updates;
      return { channelConfigs: nextConfigs };
    });
  };

  const updateActiveChannelConfig = (
    updates: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)
  ) => {
    if (!activeChannelId) return;
    updateChannelConfigs((prev) => {
      const activeMonitor = monitorOptions.find((monitor) => monitor.id === activeChannelId);
      const prevConfig =
        prev[activeChannelId] ??
        createChannelConfig(scheduleData.params ?? {}, activeChannelId, activeMonitor?.ip ?? '', slotActiveConfigs[activeChannelId]);
      const nextConfig = typeof updates === 'function' ? updates(prevConfig) : updates;
      return { ...prev, [activeChannelId]: { ...prevConfig, ...nextConfig } };
    });
  };

  const handleAdvancedConfigChange = (updates: Partial<AdvancedConfig>) => {
    updateActiveChannelConfig((prev) => {
      const result: Record<string, any> = { ...prev };

      if ('taskType' in updates) result.taskType = updates.taskType;
      if ('realtime' in updates) result.realtime = updates.realtime;
      if ('profile' in updates) result.profile = updates.profile;
      if ('filename' in updates) result.filename = updates.filename;
      if ('additionalArgs' in updates) {
        result.additionalArgs = (updates.additionalArgs ?? '')
          .split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      }
      if ('heartbeatFile' in updates) result.heartbeatFile = updates.heartbeatFile;
      if ('heartbeatIntervalMs' in updates) result.heartbeatIntervalMs = updates.heartbeatIntervalMs;
      if ('heartbeatMessage' in updates) result.heartbeatMessage = updates.heartbeatMessage;

      const inputChanged = 'inputType' in updates || 'input' in updates || 'inputResourceId' in updates;
      if (inputChanged) {
        const newInputType = updates.inputType ?? activeAdvancedConfig.inputType;
        const isSdi = newInputType === 'sdi';
        if (isSdi) {
          const resourceId = 'inputResourceId' in updates
            ? (updates.inputResourceId ?? '')
            : activeAdvancedConfig.inputResourceId;
          const resource = sdiResources.find((r) => r.id === resourceId);
          const deviceIndex = resource?.config?.deviceIndex?.toString() ?? '';
          const { id: _removedInputId, ...prevInputWithoutId } = (prev.input ?? {}) as Record<string, any>;
          result.input = {
            ...prevInputWithoutId,
            ...(resourceId ? { id: resourceId } : {}),
            type: 'sdi',
            url: deviceIndex ? buildSdiInputUrl(deviceIndex) : '',
            video: { sourceId: deviceIndex, formatId: null },
            audio: { sourceId: '', formatId: null },
          };
          result.inputResourceId = resourceId;
        } else {
          const newUrl = 'input' in updates ? (updates.input ?? '') : (prev.input?.url ?? '');
          const { id: _removedInputId, ...prevInputWithoutId } = (prev.input ?? {}) as Record<string, any>;
          result.input = {
            ...prevInputWithoutId,
            type: newInputType,
            url: newUrl,
          };
          result.inputResourceId = '';
        }
      }

      const outputChanged = 'outputResourceId' in updates || 'output' in updates || 'outputType' in updates;
      if (outputChanged) {
        if ('outputResourceId' in updates) {
          const resourceId = updates.outputResourceId ?? '';
          result.outputResourceId = resourceId;
          if (resourceId) {
            const resource = filesystemResources.find((r) => r.id === resourceId);
            result.output = {
              type: 'file',
              url: resource?.path ?? '',
            };
          }
        }
        if ('output' in updates) {
          result.output = { ...(prev.output ?? {}), url: updates.output ?? '', type: prev.output?.type ?? 'file' };
          result.outputResourceId = '';
        }
        if ('outputType' in updates) {
          result.output = { ...(prev.output ?? {}), type: 'file' };
        }
      }

      const additionalOutputChanged =
        'additionalOutputEnabled' in updates ||
        'additionalOutputResourceId' in updates ||
        'additionalOutputProfile' in updates;
      if (additionalOutputChanged) {
        const enabled = updates.additionalOutputEnabled ?? activeAdvancedConfig.additionalOutputEnabled;
        const resourceId = updates.additionalOutputResourceId ?? activeAdvancedConfig.additionalOutputResourceId;
        const profile = updates.additionalOutputProfile ?? activeAdvancedConfig.additionalOutputProfile;
        result.additionalOutputEnabled = enabled;
        result.additionalOutputResourceId = resourceId;
        result.additionalOutputProfile = profile;
        result.additionalOutputs = enabled && resourceId
          ? [{ output: { id: resourceId }, profile }]
          : [];
      }

      return result;
    });
  };

  const updateRetryPolicy = (
    updates: IScheduleRetryPolicy | ((prev: IScheduleRetryPolicy) => IScheduleRetryPolicy)
  ) => {
    setScheduleData((prev) => {
      const base = ensureScheduleState(prev);
      const prevPolicy = { ...(base.retryPolicy ?? {}) } as IScheduleRetryPolicy;
      const nextPolicy = typeof updates === 'function' ? updates(prevPolicy) : { ...updates };
      return { ...base, retryPolicy: { ...prevPolicy, ...nextPolicy } };
    });
  };

  const updateAdvancedScheduleConfig = (updates: Partial<AdvancedScheduleConfig>) => {
    if ('enabled' in updates) {
      updateSchedule({ enabled: updates.enabled });
    }
    if ('tz' in updates) {
      updateSchedule({ tz: updates.tz });
    }
    if ('overlapPolicy' in updates) {
      updateSchedule({ overlapPolicy: updates.overlapPolicy });
    }
    if ('misfirePolicy' in updates) {
      updateSchedule({ misfirePolicy: updates.misfirePolicy });
    }

    const retryPolicyUpdates: Partial<IScheduleRetryPolicy> = {};
    if ('retryMaxAttempts' in updates) retryPolicyUpdates.maxAttempts = updates.retryMaxAttempts;
    if ('retryInitialMs' in updates) retryPolicyUpdates.initialMs = updates.retryInitialMs;
    if ('retryBackoff' in updates) retryPolicyUpdates.backoff = updates.retryBackoff;
    if (Object.keys(retryPolicyUpdates).length > 0) {
      updateRetryPolicy(retryPolicyUpdates);
    }
  };

  const hydrateRecordState = (nextRecord: ITimelineItem | null, scheduleSource?: Partial<ISchedule>) => {
    setFormData(nextRecord);
    setShowErrors(false);

    if (nextRecord) {
      const metadata = {
        ...((nextRecord.options?.metadata ?? {}) as Partial<ISchedule>),
        ...(scheduleSource ?? {}),
      };
      const base = createScheduleState();
      const mergedParams = {
        ...(base.params ?? {}),
        ...((metadata?.params as Record<string, any>) ?? {}),
      };
      const mergedRetryPolicy: IScheduleRetryPolicy = {
        ...(base.retryPolicy ?? {}),
        ...((metadata?.retryPolicy as IScheduleRetryPolicy) ?? {}),
      };

      if (!mergedParams.monitorId && nextRecord.channel) {
        mergedParams.monitorId = nextRecord.channel;
      }

      const draftSelectedChannelIds =
        nextRecord.id === 'new_0' && initialSelectedChannelIds.length > 0
          ? initialSelectedChannelIds
          : [];

      const resolvedSelectedChannelIds = [
        ...draftSelectedChannelIds,
        ...normalizeMonitorIds(mergedParams.monitorIds),
        ...extractMonitorIdsFromAdditionalArgs(mergedParams.additionalArgs),
        extractMonitorIdFromAdditionalArgs(mergedParams.additionalArgs),
        metadata.slotId,
        mergedParams.monitorId,
        nextRecord.channel,
      ]
        .map((item) => (item ?? '').toString().trim())
        .filter((item, index, array) => item.length > 0 && array.indexOf(item) === index);

      const resolvedTaskType = metadata?.taskType ?? base.taskType;
      mergedParams.taskType = resolvedTaskType;
      const existingChannelConfigs = ((mergedParams.channelConfigs as Record<string, Record<string, any>>) ?? {});
      if (resolvedTaskType === MEDIAENGINE_TASK_TYPE) {
        const legacyInput = mergedParams.input;
        const legacyOutput = mergedParams.output;
        const legacyMonitorId =
          mergedParams.monitorId ||
          extractMonitorIdFromAdditionalArgs(mergedParams.additionalArgs) ||
          metadata.slotId ||
          nextRecord.channel || '';

        const normalizedInput =
          legacyInput && typeof legacyInput === 'object' && !Array.isArray(legacyInput)
            ? { ...legacyInput } : {};
        const normalizedOutput =
          legacyOutput && typeof legacyOutput === 'object' && !Array.isArray(legacyOutput)
            ? { ...legacyOutput } : {};

        if (!normalizedInput.type) normalizedInput.type = 'rtmp';
        if (!normalizedInput.url) {
          normalizedInput.url = mergedParams.inputUrl ?? (typeof legacyInput === 'string' ? legacyInput : '');
        }

        const sdiDeviceIndex = parseSdiDeviceIndexFromUrl(normalizedInput.url);
        const looksLikeSdiUrl = normalizedInput.url.toString().trim().toLowerCase().startsWith('sdi://');
        if (looksLikeSdiUrl && normalizedInput.type === 'rtmp') normalizedInput.type = 'sdi';

        normalizedInput.video = {
          ...(normalizedInput.video ?? {}),
          sourceId: normalizedInput.video?.sourceId ?? legacyMonitorId,
          formatId: normalizedInput.video?.formatId ?? null,
        };
        normalizedInput.audio = {
          ...(normalizedInput.audio ?? {}),
          sourceId: normalizedInput.audio?.sourceId ?? '',
          formatId: normalizedInput.audio?.formatId ?? null,
        };

        if (normalizedInput.type === 'sdi') {
          if (sdiDeviceIndex) {
            normalizedInput.video = { ...(normalizedInput.video ?? {}), sourceId: sdiDeviceIndex, formatId: null };
          } else {
            normalizedInput.video = { ...(normalizedInput.video ?? {}), formatId: null };
          }
          normalizedInput.audio = { ...(normalizedInput.audio ?? {}), sourceId: '', formatId: null };
        }

        if (!normalizedOutput.type) normalizedOutput.type = 'file';
        if (!normalizedOutput.url) {
          if (typeof legacyOutput === 'string' && legacyOutput.trim().length > 0) {
            normalizedOutput.url = legacyOutput;
          } else {
            normalizedOutput.url = buildOutputPath(
              (mergedParams.outputDir ?? '').toString(),
              (mergedParams.outputFilename ?? '').toString()
            );
          }
        }

        mergedParams.input = normalizedInput;
        mergedParams.output = normalizedOutput;
        mergedParams.realtime = typeof mergedParams.realtime === 'boolean' ? mergedParams.realtime : true;
        mergedParams.additionalArgs = Array.isArray(mergedParams.additionalArgs)
          ? mergedParams.additionalArgs
          : Array.isArray(mergedParams.extra) ? mergedParams.extra : [];

        if (!mergedParams.monitorId) {
          const monitorIdFromInput = mergedParams.input?.video?.sourceId;
          if (monitorIdFromInput) mergedParams.monitorId = monitorIdFromInput;
        }
      }

      mergedParams.monitorIds =
        resolvedSelectedChannelIds.length > 0
          ? resolvedSelectedChannelIds
          : mergedParams.monitorId ? [mergedParams.monitorId.toString()] : [];

      mergedParams.channelConfigs = mergedParams.monitorIds.reduce((acc, channelId) => {
        const monitor = monitorOptions.find((item) => item.id === channelId);
        if (existingChannelConfigs[channelId]) {
          const cfg = existingChannelConfigs[channelId];
          acc[channelId] = {
            ...cfg,
            outputResourceId: (cfg.outputResourceId ?? cfg.output?.id ?? '').toString(),
            inputResourceId: (cfg.inputResourceId ?? cfg.input?.id ?? '').toString(),
          };
        } else {
          acc[channelId] = createChannelConfig(mergedParams, channelId, monitor?.ip ?? '', slotActiveConfigs[channelId]);
        }
        return acc;
      }, {} as Record<string, Record<string, any>>);

      const resolvedRecordMonitor = nextRecord.channel ? monitorOptions.find((m) => m.id === nextRecord.channel) : undefined;
      if (resolvedTaskType === MEDIAENGINE_TASK_TYPE) {
        if (!mergedParams.input?.url && resolvedRecordMonitor?.ip) {
          mergedParams.input = { ...(mergedParams.input ?? {}), url: resolvedRecordMonitor.ip };
        }
        if (!mergedParams.output?.url && defaultScheduleMetadata.params?.output?.url) {
          mergedParams.output = { ...(mergedParams.output ?? {}), url: defaultScheduleMetadata.params.output.url };
        }
      }

      const scheduleState: Partial<ISchedule> = {
        ...base,
        ...metadata,
        label: metadata?.label ?? nextRecord.title ?? base.label,
        taskType: metadata?.taskType ?? base.taskType,
        tz: metadata?.tz ?? base.tz,
        overlapPolicy: metadata?.overlapPolicy ?? base.overlapPolicy,
        misfirePolicy: metadata?.misfirePolicy ?? base.misfirePolicy,
        enabled: typeof metadata?.enabled === 'boolean' ? metadata.enabled : base.enabled,
        startAt: metadata?.startAt ?? nextRecord.start,
        endAt: metadata?.endAt ?? nextRecord.end,
        params: mergedParams,
        retryPolicy: mergedRetryPolicy,
      };

      setScheduleData(scheduleState);
      setSelectedChannelIds(mergedParams.monitorIds);
      setActiveSettingsChannelId(mergedParams.monitorIds[0] ?? '');
    } else {
      setScheduleData(createScheduleState());
      setSelectedChannelIds(
        initialSelectedChannelIds.length > 0
          ? initialSelectedChannelIds
          : nextRecord?.channel ? [nextRecord.channel] : []
      );
      setActiveSettingsChannelId(
        initialSelectedChannelIds[0] ?? nextRecord?.channel ?? ''
      );
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!open) return;

      const isNewRecord = !record || record.id === 'new_0';
      const metadata = (record?.options?.metadata ?? {}) as Partial<ISchedule>;
      const scheduleId = (metadata.scheduleId ?? record?.id ?? '').toString().trim();

      if (isNewRecord || !token || !scheduleId) {
        hydrateRecordState(record);
        setLoadingInitialSchedule(false);
        return;
      }

      hydrateRecordState(record);
      setLoadingInitialSchedule(true);

      try {
        const response = await axios.get(
          `${GatewayIp + endpoints.scheduler.schedules}/${scheduleId}`,
          {
            withCredentials: true,
            headers: authHeaders(token),
          }
        );

        if (!cancelled) {
          hydrateRecordState(record, response.data as ISchedule);
        }
      } catch {
        if (!cancelled) {
          hydrateRecordState(record);
        }
      } finally {
        if (!cancelled) {
          setLoadingInitialSchedule(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      setFormData(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedChannelIds, open, record, token]);

  useEffect(() => {
    const start = record?.start ? moment.utc(record.start) : null;
    const end = record?.end ? moment.utc(record.end) : null;
    if (start && end && end.diff(start, 'seconds') > 0) {
      setStartDate(start.local().toISOString());
      setStartsAt(start.local().format('HH:mm:ss'));
      setEndDate(end.local().toISOString());
      setEndsAt(end.local().format('HH:mm:ss'));
    }
  }, [record?.start, record?.end]);

  useEffect(() => {
    if (!open) setShowErrors(false);
  }, [open]);

  if (!formData) return null;

  const handleTitleChange = (value: string) => {
    setFormField('title', value);
    updateSchedule({ label: value });
  };

  const syncSelectedChannels = (nextSelectedChannelIds: string[]) => {
    const previousPrimaryChannelId = selectedChannelIds[0] ?? '';
    const nextPrimaryChannelId = nextSelectedChannelIds[0] ?? '';
    const primaryMonitor = monitorOptions.find((monitor) => monitor.id === nextPrimaryChannelId);
    const previousPrimaryMonitor = monitorOptions.find((monitor) => monitor.id === previousPrimaryChannelId);

    setSelectedChannelIds(nextSelectedChannelIds);
    setActiveSettingsChannelId((current) =>
      nextSelectedChannelIds.includes(current) ? current : (nextSelectedChannelIds[0] ?? '')
    );
    setFormField('channel', nextPrimaryChannelId);

    updateScheduleParams((prev) => {
      const activeConfig = activeAdvancedConfig;
      const isSdi = activeConfig.inputType === 'sdi';
      const previousInputUrl = (prev.input?.url ?? '').toString();
      const shouldReplaceInputUrl =
        !previousInputUrl ||
        previousInputUrl === (defaultScheduleMetadata.params?.input?.url ?? '') ||
        previousInputUrl === (previousPrimaryMonitor?.ip ?? '');

      return {
        monitorId: nextPrimaryChannelId,
        monitorIds: nextSelectedChannelIds,
        channelConfigs: nextSelectedChannelIds.reduce<Record<string, Record<string, any>>>((acc, channelId) => {
          const monitor = monitorOptions.find((item) => item.id === channelId);
          const existingConfig = (prev.channelConfigs as Record<string, Record<string, any>> | undefined)?.[channelId];
          acc[channelId] = existingConfig ?? createChannelConfig(prev, channelId, monitor?.ip ?? '', slotActiveConfigs[channelId]);
          return acc;
        }, {}),
        input: {
          ...(prev.input ?? {}),
          url: !isSdi && shouldReplaceInputUrl ? primaryMonitor?.ip ?? '' : prev.input?.url,
          video: {
            ...(prev.input?.video ?? {}),
            sourceId: isSdi ? (prev.input?.video?.sourceId ?? '') : nextPrimaryChannelId,
          },
        },
      };
    });
  };

  const handleChannelToggle = (channelId: string, checked: boolean) => {
    const nextSelectedChannelIds = checked
      ? [...selectedChannelIds, channelId]
      : selectedChannelIds.filter((id) => id !== channelId);
    syncSelectedChannels(nextSelectedChannelIds);
  };

  const handleToggleAllChannels = (checked: boolean) => {
    syncSelectedChannels(checked ? visibleMonitorOptions.map((monitor) => monitor.id) : []);
  };

  const handleTimeChange = (nextStart?: string, nextEnd?: string) => {
    if (!nextStart || !nextEnd) return;
    setScheduleData((prev) => ({ ...ensureScheduleState(prev), startAt: nextStart, endAt: nextEnd }));
    setFormData((prev) => prev ? { ...prev, start: nextStart, end: nextEnd } : prev);
  };

  const onTimeChanged = (override?: { startDate?: string; startTime?: string; endDate?: string; endTime?: string }) => {
    const startDateValue = override?.startDate ?? startDate;
    const startTimeValue = override?.startTime ?? startsAt;
    const endDateValue = override?.endDate ?? endDate;
    const endTimeValue = override?.endTime ?? endsAt;
    if (!startDateValue || !startTimeValue || !endDateValue || !endTimeValue) return;

    const start = moment(`${moment(startDateValue).format('YYYY-MM-DD')} ${startTimeValue}`).utc().toISOString();
    const end = moment(`${moment(endDateValue).format('YYYY-MM-DD')} ${endTimeValue}`).utc().toISOString();

    if (start && end) {
      setStartsAt(moment.utc(start).local().format('HH:mm:ss'));
      setEndsAt(moment.utc(end).local().format('HH:mm:ss'));
      handleTimeChange(start, end);
    }
  };

  const focusFirstInvalidField = () => {
    if (!validation.label) { titleInputRef.current?.focus(); return; }
    if (!validation.monitor) { document.getElementById('record-monitor-select-all')?.focus(); return; }
    if (!validation.start) { document.getElementById('picker_startdate')?.focus(); startTimeRef.current?.focus(); return; }
    if (!validation.end) { document.getElementById('picker_enddate')?.focus(); endTimeRef.current?.focus(); return; }
  };

  const handleSubmit = () => {
    if (!formData) return;
    if (!isFormValid) { setShowErrors(true); focusFirstInvalidField(); return; }
    setShowErrors(false);

    const params: Record<string, any> = {
      ...(scheduleData.params ?? {}),
      monitorId: primaryChannelId || scheduleData.params?.monitorId || formData.channel || '',
      monitorIds: selectedChannelIds,
    };

    const retryPolicy: IScheduleRetryPolicy = {
      ...(defaultScheduleMetadata.retryPolicy ?? {}),
      ...(scheduleData.retryPolicy ?? {}),
    };
    if (retryPolicy.maxAttempts !== undefined) retryPolicy.maxAttempts = Number(retryPolicy.maxAttempts);
    if (retryPolicy.initialMs !== undefined) retryPolicy.initialMs = Number(retryPolicy.initialMs);

    const payloadChannelConfigs = selectedChannelIds.reduce<Record<string, Record<string, any>>>((acc, channelId) => {
      const monitor = monitorOptions.find((item) => item.id === channelId);
      const config =
        channelConfigs[channelId] ??
        createChannelConfig(scheduleData.params ?? {}, channelId, monitor?.ip ?? '', slotActiveConfigs[channelId]);
      const channelTaskType = (config?.taskType ?? scheduleData.taskType ?? defaultScheduleMetadata.taskType ?? MEDIAENGINE_TASK_TYPE).toString();
      const configInputType = (config?.input?.type ?? 'rtmp').toString().trim();
      const configIsSdi = configInputType === 'sdi';
      const channelAdditionalArgs = Array.isArray(config?.additionalArgs)
        ? config.additionalArgs
        : typeof config?.additionalArgs === 'string'
          ? config.additionalArgs.split(/\r?\n/).map((line: string) => line.trim()).filter(Boolean)
          : [];

      const outputValue = (config?.outputResourceId ?? '').toString().trim().length > 0
        ? { id: config.outputResourceId }
        : {
            type: 'file',
            url: (config?.output?.url ?? '').toString().trim(),
          };

      acc[channelId] = {
        taskType: channelTaskType,
        input: {
          ...(configIsSdi && (config?.inputResourceId ?? config?.input?.id)
            ? { id: (config?.inputResourceId ?? config?.input?.id).toString().trim() }
            : {}),
          type: configInputType,
          url: configIsSdi
            ? buildSdiInputUrl(config?.input?.video?.sourceId)
            : (config?.input?.url ?? '').toString().trim(),
          video: {
            sourceId: (config?.input?.video?.sourceId ?? channelId).toString().trim(),
            formatId: configIsSdi ? null : normalizeNullableString(config?.input?.video?.formatId),
          },
          audio: {
            sourceId: configIsSdi ? '' : (config?.input?.audio?.sourceId ?? '').toString().trim(),
            formatId: configIsSdi ? null : normalizeNullableString(config?.input?.audio?.formatId),
          },
        },
        output: outputValue,
        additionalOutputs: Array.isArray(config?.additionalOutputs) ? config.additionalOutputs : [],
        filename: (config?.filename ?? '').toString().trim(),
        profile: (config?.profile ?? '').toString(),
        realtime: typeof config?.realtime === 'boolean' ? config.realtime : true,
        additionalArgs: channelAdditionalArgs,
        heartbeatFile: (config?.heartbeatFile ?? DEFAULT_HEARTBEAT_FILE).toString(),
        heartbeatIntervalMs: Number(config?.heartbeatIntervalMs ?? 1000),
        heartbeatMessage: (config?.heartbeatMessage ?? DEFAULT_HEARTBEAT_MESSAGE).toString(),
      };

      return acc;
    }, {});

    const primaryChannelConfig = payloadChannelConfigs[primaryChannelId] ?? payloadChannelConfigs[selectedChannelIds[0] ?? ''];
    const resolvedTaskType = (primaryChannelConfig?.taskType ?? scheduleData.taskType ?? defaultScheduleMetadata.taskType ?? MEDIAENGINE_TASK_TYPE).toString();

    const payloadParams =
      resolvedTaskType === MEDIAENGINE_TASK_TYPE
        ? {
            monitorId: (params.monitorId ?? formData.channel ?? '').toString().trim(),
            monitorIds: normalizeMonitorIds(params.monitorIds),
            input: primaryChannelConfig?.input,
            output: primaryChannelConfig?.output,
            additionalOutputs: primaryChannelConfig?.additionalOutputs ?? [],
            filename: (primaryChannelConfig?.filename ?? '').toString().trim(),
            profile: primaryChannelConfig?.profile ?? '',
            realtime: typeof primaryChannelConfig?.realtime === 'boolean' ? primaryChannelConfig.realtime : true,
            channelConfigs: payloadChannelConfigs,
            additionalArgs: primaryChannelConfig?.additionalArgs ?? [],
          }
        : resolvedTaskType === DUMMY_HEARTBEAT_TASK_TYPE
          ? {
              monitorId: (params.monitorId ?? formData.channel ?? '').toString().trim(),
              monitorIds: normalizeMonitorIds(params.monitorIds),
              channelConfigs: payloadChannelConfigs,
              file: primaryChannelConfig?.heartbeatFile ?? DEFAULT_HEARTBEAT_FILE,
              intervalMs: Number(primaryChannelConfig?.heartbeatIntervalMs ?? 1000),
              message: primaryChannelConfig?.heartbeatMessage ?? DEFAULT_HEARTBEAT_MESSAGE,
            }
          : {
              monitorId: (params.monitorId ?? formData.channel ?? '').toString().trim(),
              monitorIds: normalizeMonitorIds(params.monitorIds),
              channelConfigs: payloadChannelConfigs,
              input: primaryChannelConfig?.input?.url ?? '',
              inputUrl: primaryChannelConfig?.input?.url ?? '',
              output: typeof primaryChannelConfig?.output === 'object'
                ? (primaryChannelConfig.output?.url ?? '')
                : (primaryChannelConfig?.output ?? ''),
              profile: primaryChannelConfig?.profile ?? '',
              realtime: typeof primaryChannelConfig?.realtime === 'boolean' ? primaryChannelConfig.realtime : true,
              additionalArgs: primaryChannelConfig?.additionalArgs ?? [],
            };

    const schedulePayload: Partial<ISchedule> = {
      ...scheduleData,
      label: scheduleData.label ?? formData.title,
      taskType: resolvedTaskType,
      startAt: toUtcIsoString(formData.start),
      endAt: toUtcIsoString(formData.end),
      tz: scheduleData.tz ?? defaultScheduleMetadata.tz,
      overlapPolicy: scheduleData.overlapPolicy ?? defaultScheduleMetadata.overlapPolicy,
      misfirePolicy: scheduleData.misfirePolicy ?? defaultScheduleMetadata.misfirePolicy,
      enabled: typeof scheduleData.enabled === 'boolean' ? scheduleData.enabled : defaultScheduleMetadata.enabled,
      params: payloadParams,
      retryPolicy,
    };

    const updatedFormData: ITimelineItem = {
      ...formData,
      title: schedulePayload.label ?? formData.title,
      options: { ...(formData.options ?? {}), metadata: schedulePayload },
    };

    onSubmit(updatedFormData, schedulePayload);
    onClose();
  };

  return (
    <Modal
      className={hasDestructiveSecondaryAction ? 'record-modal--danger-secondary' : undefined}
      open={open}
      modalHeading={formData.id === 'new_0' ? 'Create New Record' : 'Edit Schedule'}
      primaryButtonText={formData.id === 'new_0' ? 'Create' : 'Update'}
      primaryButtonDisabled={isSubmitDisabled}
      hasScrollingContent
      selectorPrimaryFocus="#record-name"
      secondaryButtons={secondaryButtons}
      onRequestClose={onClose}
      onRequestSubmit={handleSubmit}
      preventCloseOnClickOutside={true}
    >
      <style>
        {`
          .record-modal--danger-secondary .cds--modal-footer > .cds--btn--secondary:last-of-type {
            background-color: #da1e28; border-color: #da1e28; color: #ffffff;
          }
          .record-modal--danger-secondary .cds--modal-footer > .cds--btn--secondary:last-of-type:hover {
            background-color: #b81921; border-color: #b81921;
          }
          .record-modal--danger-secondary .cds--modal-footer > .cds--btn--secondary:last-of-type:focus {
            box-shadow: inset 0 0 0 1px #ffffff, inset 0 0 0 3px #0f62fe;
          }
        `}
      </style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '60vh', overflowY: 'auto', paddingRight: '1rem' }}>

        <TextInput
          id="record-name"
          ref={titleInputRef}
          labelText="Name"
          value={formData.title ?? ''}
          readOnly={!canEditAllInputs}
          onChange={(e) => handleTitleChange(e.target.value)}
          invalid={showErrors && !validation.label}
          invalidText="Name is required"
        />

        <div>
          <div style={{ fontSize: 12, marginBottom: 8, color: '#c6c6c6' }}>Channels</div>
          <div style={{ border: `1px solid ${showErrors && !validation.monitor ? '#da1e28' : '#525252'}`, borderRadius: 6, padding: 12 }}>
            <Checkbox
              id="record-monitor-select-all"
              labelText="Select all available channels"
              checked={allVisibleChannelsSelected}
              disabled={!canEditAllInputs}
              onChange={(_, { checked }) => handleToggleAllChannels(Boolean(checked))}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 12 }}>
              {visibleMonitorOptions.map((monitor) => (
                <Checkbox
                  key={monitor.id}
                  id={`record-monitor-${monitor.id}`}
                  labelText={monitor.title}
                  checked={selectedChannelIds.includes(monitor.id)}
                  disabled={!canEditAllInputs}
                  onChange={(_, { checked }) => handleChannelToggle(monitor.id, Boolean(checked))}
                />
              ))}
            </div>
          </div>
          {showErrors && !validation.monitor ? (
            <div style={{ fontSize: 12, marginTop: 4, color: '#da1e28' }}>Select at least one channel</div>
          ) : null}
        </div>

        {selectedChannelIds.length > 0 && (
          <div>
            <div style={{ fontSize: 12, marginBottom: 8, color: '#c6c6c6' }}>Channel Settings</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selectedChannelIds.map((channelId) => {
                const monitor = monitorOptions.find((item) => item.id === channelId);
                return (
                  <button
                    key={channelId}
                    type="button"
                    disabled={!canEditAllInputs}
                    onClick={() => setActiveSettingsChannelId(channelId)}
                    style={{
                      border: `1px solid ${activeChannelId === channelId ? '#0f62fe' : '#525252'}`,
                      backgroundColor: activeChannelId === channelId ? '#0f62fe' : '#262626',
                      color: '#fff',
                      borderRadius: 6,
                      padding: '6px 10px',
                      cursor: !canEditAllInputs ? 'default' : 'pointer',
                      opacity: !canEditAllInputs ? 0.7 : 1,
                    }}
                  >
                    {monitor?.title ?? channelId}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ display: 'flex', width: '100%', marginBottom: 20 }}>
            <XDDatePicker
              id="startdate"
              labelText="Start Date"
              value={startDate}
              readOnly={!canEditAllInputs}
              disabled={!canEditAllInputs}
              style={{ marginBottom: 20, width: '45%' }}
              onChange={(value) => { setStartDate(value[0]); onTimeChanged({ startDate: value[0] }); }}
              invalid={showErrors && !validation.start}
              invalidText={canEditAllInputs ? "Start date must be in the future" : "Start date is required"}
            />
            <div style={{ width: 10 }} />
            <TimePicker
              id="start-time-picker"
              ref={startTimeRef}
              pattern="(1[012]|[1-9]):[0-5][0-9]:[0-5][0-9](\\s)?"
              maxLength={8}
              style={{ width: '100%' }}
              labelText="Start Time"
              value={startsAt}
              disabled={!canEditAllInputs}
              onChange={(e) => setStartsAt(e.target?.value)}
              onBlur={() => onTimeChanged()}
              invalid={showErrors && !validation.start}
              invalidText={canEditAllInputs ? "Start time must be in the future" : "Provide a valid start time"}
            />
          </div>
          <div style={{ width: 20 }} />
          <div style={{ display: 'flex', width: '100%', marginBottom: 20 }}>
            <XDDatePicker
              id="enddate"
              labelText="End Date"
              value={endDate}
              readOnly={!canEditEndAt}
              disabled={!canEditEndAt}
              style={{ marginBottom: 20, width: '45%' }}
              onChange={(value) => { setEndDate(value[0]); onTimeChanged({ endDate: value[0] }); }}
              invalid={showErrors && !validation.end}
              invalidText="End date must be after start date"
            />
            <div style={{ width: 10 }} />
            <TimePicker
              id="end-time-picker"
              ref={endTimeRef}
              pattern="(1[012]|[1-9]):[0-5][0-9]:[0-5][0-9](\\s)?"
              maxLength={8}
              style={{ width: '100%' }}
              labelText="End Time"
              value={endsAt}
              disabled={!canEditEndAt}
              onChange={(e) => setEndsAt(e.target?.value)}
              onBlur={() => onTimeChanged()}
              invalid={showErrors && !validation.end}
              invalidText={canEditEndAt ? "End time must be after start time and in the future" : "End time must be after start time"}
            />
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#8d8d8d', marginTop: -12, marginBottom: 4 }}>
          Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
        </div>

        <RecorderAdvancedConfigForm
          value={activeAdvancedConfig}
          onChange={handleAdvancedConfigChange}
          sdiResources={sdiResources}
          filesystemResources={filesystemResources}
          rtmpResources={rtmpResources}
          mediaengineProfiles={profileOptions}
          showFilename={true}
          readOnly={!canEditAllInputs}
          scheduleConfig={{
            enabled: typeof scheduleData.enabled === 'boolean' ? scheduleData.enabled : Boolean(defaultScheduleMetadata.enabled),
            tz: scheduleData.tz ?? defaultScheduleMetadata.tz ?? '',
            overlapPolicy: scheduleData.overlapPolicy ?? defaultScheduleMetadata.overlapPolicy ?? '',
            misfirePolicy: scheduleData.misfirePolicy ?? defaultScheduleMetadata.misfirePolicy ?? '',
            retryMaxAttempts: Number(scheduleData.retryPolicy?.maxAttempts ?? defaultScheduleMetadata.retryPolicy?.maxAttempts ?? 0),
            retryInitialMs: Number(scheduleData.retryPolicy?.initialMs ?? defaultScheduleMetadata.retryPolicy?.initialMs ?? 0),
            retryBackoff: scheduleData.retryPolicy?.backoff ?? defaultScheduleMetadata.retryPolicy?.backoff ?? 'exponential',
          }}
          onScheduleConfigChange={updateAdvancedScheduleConfig}
          scheduleReadOnly={!canEditAllInputs}
          idPrefix={`record-cfg-${activeChannelId}`}
        />

        {loadingInitialSchedule ? (
          <div style={{ fontSize: 12, color: '#8d8d8d' }}>Refreshing schedule details…</div>
        ) : null}

      </div>
    </Modal>
  );
};

export { CreateRecordModal };
